// App Configuration
export const APP_CONFIG = {
  name: 'Check-in Bot',
  email: 'support@checkinbot.io',
  company: 'Thrumm, LLC',
  year: '2025',
  description: 'Stay Connected Without the Pressure',
  supportUrl: 'https://checkinbot.io/help',
  iosAppStoreUrl: 'https://apps.apple.com/us/app/check-in-bot/id6754995125'
};

// Legal documents last updated dates
// Note: Month is 0-indexed (9 = October, 8 = September)
export const LAST_UPDATED_TERMS = new Date(2025, 9, 30).toLocaleDateString('en-US', { 
  year: 'numeric', 
  month: 'long', 
  day: 'numeric' 
});

export const LAST_UPDATED_PRIVACY = new Date(2025, 9, 30).toLocaleDateString('en-US', { 
  year: 'numeric', 
  month: 'long', 
  day: 'numeric' 
});

// Navigation items
export const NAV_ITEMS = [
  { label: 'Home', path: '/' },
  { label: 'Pricing', path: '/pricing' },
  { label: 'Help Center', path: '/help' }
];

// Pricing plans
export const PRICING_PLANS = [
  {
    name: 'Free',
    price: '$0',
    period: 'Forever',
    features: [
      'Automated check-ins',
      'Join unlimited groups',
      'Share check-ins with group members',
      'Notifications on missed check-ins',
      'View group member check-in statuses'
    ]
  },
  {
    name: 'Duo',
    price: '$2.99',
    period: 'Per month',
    features: [
      'Everything in Free',
      'Host groups',
      '1 group, up to 2 members'
    ]
  },
  {
    name: 'Family',
    price: '$4.99',
    period: 'Per month',
    features: [
      'Everything in Free',
      'Host groups',
      '2 groups, up to 5 members each'
    ]
  },
  {
    name: 'Family Plus',
    price: '$6.99',
    period: 'Per month',
    features: [
      'Everything in Free',
      'Host groups',
      '4 groups, up to 10 members each'
    ]
  }
];

// Help Center data organized by topics
export const HELP_TOPICS = [
  {
    id: 'check-ins',
    title: 'How Check-ins Work',
    description: 'Understanding the basics of check-ins and how they work',
    articles: [
      {
        id: 'what-are-checkins',
        title: 'What are check-ins?',
        content: `Check-ins are daily updates that let your loved ones know you're okay. Each day, you'll register your check-in status (checked in or missed). This daily status can then be shared automatically with the people you choose.`
      },
      {
        id: 'manual-vs-automatic',
        title: 'Manual vs Automatic Check-ins',
        content: 'You can choose to check in manually each day or set up automatic check-ins. Manual check-ins require you to open the app and tap a button. Automatic check-ins happen when you perform a daily activity you\'ve selected, making the process completely effortless.'
      },
      {
        id: 'missed-checkins',
        title: 'What happens if I miss a check-in?',
        content: 'If you miss a check-in, those you are sharing with will receive a notification after your check-in period ends. This gives them a heads-up to reach out and make sure you\'re okay. They can also see your check-in history and see when you last checked in.'
      },
      {
        id: 'checkin-period',
        title: 'What is the daily check-in period?',
        content: 'The check-in period depends on your timezone. Your timezone is defaulted to your device\'s timezone when you sign up and can be changed in profile settings. You\'re check-in status for a day depends on if you check-in before midnight in your selected timezone.'
      }
    ]
  },
  {
    id: 'setup-automation',
    title: 'Setting Up Automatic Check-ins',
    description: 'How to configure automated check-ins using iOS Shortcuts',
    articles: [
      {
        id: 'how-automatic-checkins-work',
        title: 'How do automatic check-ins work?',
        content: `We make use of <a href="https://support.apple.com/guide/shortcuts/intro-to-personal-automation-apd690170742/ios" target="_blank" rel="noopener noreferrer">iOS personal automations via the Shortcuts app</a> to register check-ins automatically. Apple allows you to run actions based on certain event. The action will a ${APP_CONFIG.name} action that registeres a check-in and the event will be an event you select.`
      },
      {
        id: 'setup-process',
        title: 'How to set up automatic check-ins',
        content: `During onboarding in the app, you'll be guided through the process of setting up and verifying your check-in automation. You can also see detailed instructions in the <a href="/setup-automatic-checkins" target="_blank" rel="noopener noreferrer">Setup Automatic Check-ins</a> page.`
      },
      {
        id: 'trigger-activities',
        title: 'What activity can I use to trigger my check-in?',
        content: 'There are many options but we recommend using opening an app you use daily such as email, social media, messenger, or web browser. The key is choosing something you do naturally every day. You can also select multiple events to trigger a check-in and as long as one of the events occurs, a check-in will be registered.'
      },
      {
        id: 'privacy-automation',
        title: 'Does the app or other users know how I\'m triggering my check-in?',
        content: `No, ${APP_CONFIG.name} does not know how you are triggering check-ins. We do not have access to that info and do not store or share it. We only register that a check-in occurred, not what triggered it.`
      },
      {
        id: 'change-trigger',
        title: 'Can I change my trigger activity?',
        content: 'Yes, you can change your trigger activity anytime in the iOS Shortcuts app. You can edit the existing automation or create a new one.'
      },
      {
        id: 'stop-automation',
        title: 'Can I stop automated check-ins?',
        content: 'Yes, you can stop your check-in automation anytime by deleting the automation in the iOS Shortcuts app.'
      }
    ]
  },
  {
    id: 'viewing-checkins',
    title: 'Viewing Check-ins',
    description: 'How to view your own and others\' check-in history',
    articles: [
      {
        id: 'view-history',
        title: 'How do I view my check-in status?',
        content: 'On the home tab, you can see your check-in status for the current day. You also have access to your full history of when you checked in and any missed days.'
      },
      {
        id: 'view-others',
        title: 'How do I view others\' check-ins?',
        content: 'When you join a group and there are members sharing within the group, check-ins for group members will be visible on the home tab.'
      },
      {
        id: 'view-delay',
        title: 'Why can I not see someones check-ins for the current day?',
        content: 'Check-ins are only show once the day ends in the timezone of the person you are viewing. This is done to avoid revealing exact check-in times to protect privacy.'
      },
      {
        id: 'notifications',
        title: 'What notifications will I receive?',
        content: 'If you enabled notifications, you\'ll receive notifications when any sharing member in a group you are a member of misses a check-in. You can enable/disable notifications in your device settings and app preferences. If an admin adds you to a group as an email-only member, you will receive email notifications about missed check-ins from the group.'
      }
    ]
  },
  {
    id: 'sharing',
    title: 'Sharing Check-ins',
    description: 'How to share check-ins',
    articles: [
      {
        id: 'share-checkins',
        title: 'How do I share my check-ins with others?',
        content: 'To share your check-ins with others, you\'ll need to create or be invited to a group. Once you\'re part of a group, you can choose whether to share your check-ins with the group members. This allows everyone in the group to see your check-in status according to your sharing preferences.'
      
      },
      {
        id: 'what-others-see',
        title: 'What information about my check-ins do others see?',
        content: 'When you share your check-ins with others, for each calendar day they will see if you checked in or missed. They will not see the exact time within the day that the check-in occurred or how the check-in was triggered.'
      },
      {
        id: 'multiple-groups',
        title: 'Can I share my check-ins with multiple people?',
        content: 'Yes, you can share your check-ins with multiple people by creating or joining multiple groups. Each group can have its own set of sharing members and sharing preferences.'
      },
      {
        id: 'sharing-preferences',
        title: 'How do I manage my sharing preferences?',
        content: 'You can manage your sharing preferences in the groups tab. Select the group you want to manage and you can toggle your sharing preferences on or off.'
      },
    ]
  },
  {
    id: 'groups',
    title: 'Groups',
    description: 'How to create, manage, and join groups',
    articles: [
      {
        id: 'create-group',
        title: 'How do I create a group?',
        content: 'Go to the Groups tab and tap "Create New Group". Add a group name, and optionally a profile picture and description. You\'ll also be able to invite family and friends to join via email.'
      },
      {
        id: 'max-group-create',
        title: 'How many groups can I create?',
        content: 'The max number of groups you can create depends on your subscription plan. Currently the highest plan allows you to create up 4 groups.'
      },
      {
        id: 'manage-group',
        title: 'How do I manage the group?',
        content: 'You must be the group host to manage the group. You can add or remove members, send or revoke invites, and change group details.'
      },
      {
        id: 'delete-group',
        title: 'How do I delete a group?',
        content: 'You must be the group host to delete the group. You can delete the group by tapping the "Delete Group" button in the group details.'
      },
      {
        id: 'email-only-members',
        title: 'What are email-only members?',
        content: 'When someone first get added to a group, they automatically become an email-only member. This means they\'ll receive email notifications about missed check-ins from anyone sharing check-ins in the group, even if they haven\'t signed up for the app yet. They\'ll also always have an invite to join the group by signing up for the app. If you choose to sign up and accept the invite, they\'ll become a full member with the ability to share your own check-ins, receive push notifications, and view check-in histories.'
      },
      {
        id: 'join-group',
        title: 'How do I join a group?',
        content: 'To join a group, you need to be invited by the group host. If you have been invited to a group, you can see the invite in the Groups tab. You\'ll be able to view the group details and accept or reject the invite.'
      },
      {
        id: 'max-group-join',
        title: 'How many groups can I join?',
        content: 'There is no limit on the number of groups you can join, you just need to be invited by the group host.'
      },
      {
        id: 'leave-group',
        title: 'How do I leave a group?',
        content: 'You can leave a group by tapping the group in the groups tab and then tapping the "Leave Group" button.'
      },
      {
        id: 'group-permissions',
        title: 'What can group members see?',
        content: 'Group members can see other members name, email and profile picture. If a group member is sharing their check-ins, they can see the check-in status and receive notifications about missed check-ins. Group members cannot see how the check-in was triggered or the exact time within the day that the check-in occurred. Email-only members receive email notifications about missed check-ins but cannot view check-in histories or share their own check-ins until they join the group by signing up for the app.'
      },
      {
        id: 'active-vs-inactive-groups',
        title: 'What is an active group vs inactive group?',
        content: 'Active groups allow group members to share check-ins and receive notifications and all groups are active by default when created. Inactive groups do not share check-ins and functionality is paused until the host activates them again.'
      },
      {
        id: 'why-group-inactive',
        title: 'Why is my group inactive?',
        content: 'Groups turn inactive if you previously were hosting a group but your subscription expired. Inactive groups do not count against the group host limit of your plan. Depending on your plan, you can host that many active groups.'
      }
    ]
  },
  {
    id: 'subscriptions',
    title: 'Subscriptions & Billing',
    description: 'Understanding pricing, billing, and subscription management',
    articles: [
      {
        id: 'subscription-needed',
        title: 'I want to share my check-ins with others, do I need a subscription?',
        content: 'You don\'t need a subscription to track your own check-ins. Sharing them with others, however, requires being part of a group. You can join a group for free if you receive an invite from the host, or you can create a group and invite others but creating a group requires a subscription. Only the person who creates the group needs a subscription; everyone else can join and share check-ins without one.'
      },
      {
        id: 'pricing-plans',
        title: 'What are the available plans?',
        content: 'We offer different plans depending on your needs. Check the <a href="/pricing">Pricing page</a> for current rates and what\'s included in each plan.'
      },
      {
        id: 'manage-subscription',
        title: 'How do I manage my subscription?',
        content: `Subscriptions are managed through Apple\'s App Store. To view, modify, or cancel your subscription in the ${APP_CONFIG.name} app you can go to the Settings tab > Subscription > Manage Subscription. You can also manage your subscription directly in your Apple ID settings or through the App Store app.`
      },
      {
        id: 'billing-questions',
        title: 'Who do I contact for billing questions?',
        content: 'For billing questions or refund requests, please contact Apple Support through the App Store, as all payments are processed through Apple\'s system.'
      }
    ]
  },
  {
    id: 'privacy-security',
    title: 'Privacy & Security',
    description: 'Understanding your privacy and data security',
    articles: [
      {
        id: 'location-tracking',
        title: 'Is my location data being tracked?',
        content: `No. ${APP_CONFIG.name} does not collect, store, or have access to your location data.`
      },
      {
        id: 'data-storage',
        title: 'What data do you store?',
        content: 'For detailed information about what data we collect and store, please see our <a href="/privacy">Privacy Policy</a>.'
      },
      {
        id: 'data-deletion',
        title: 'How do I delete my data?',
        content: 'You can delete your account and all associated data through the Settings tab > Advanced Options > Delete Account. Once deleted, all your data is permanently removed from our systems.'
      }
    ]
  },
  {
    id: 'device-compatibility',
    title: 'Device Availability',
    description: 'Understanding which devices are supported',
    articles: [
      {
        id: 'platforms-available',
        title: `What platforms is ${APP_CONFIG.name}  available on?`,
        content: `${APP_CONFIG.name} is only available for iOS.`
      },
      {
        id: 'iphone-android',
        title: 'Can I use this if not everyone has an iPhone?',
        content: `Yes, the app can still be used if not everyone has an iPhone, though with limited functionality. It's crucial that the people who want to share their check-ins have an iPhone as they need the app to register automated check-ins. However, their loved ones can still receive email notifications about missed check-ins when added to a group, even without the app.`
      },
      {
        id: 'android-future',
        title: 'Will there be an Android version soon?',
        content: `Not at this time due to technical limitations of the Android platform for automating check-ins. This could change in the future.`
      }
    ]
  }
];

// Features data
export const FEATURES = [
  {
    title: 'Truly Private',
    description: 'We don\'t track your location or know anything about how you are triggering check-ins. We just note a generic check-in event to share with your loved ones.'
  },
  {
    title: 'Effortless Check-ins',
    description: 'No need to send daily texts or log into an app. Your existing habits do the work for you.'
  },
  {
    title: 'Better Relationships',
    description: 'Reduce worry. Perfect for college students, adult children with aging parents, or anyone who cares from a distance.'
  },
  {
    title: 'Simple & Intuitive',
    description: 'Setup takes minutes. The app works in the background without slowing you down.'
  },
  {
    title: 'Smart Notifications',
    description: 'Your loved ones get notified when you miss a check-in, so they can reach out if needed.'
  },
];

// How it works steps for sharing check-ins
export const HOW_IT_WORKS_SHARING = [
  {
    number: 1,
    title: 'Choose Your Signal',
    description: 'Pick a daily activity you do such as checking email, opening the news app, or messaging a friend.'
  },
  {
    number: 2,
    title: 'Automatic Check-ins',
    description: `${APP_CONFIG.name} silently monitors for that activity. When it happens, a check-in is registered automatically.`
  },
  {
    number: 3,
    title: 'Share & Keep Living',
    description: 'Choose who sees your check-ins. They get peace of mind without interrupting your daily life.'
  }
];

// How it works steps for following others
export const HOW_IT_WORKS_FOLLOWING = [
  {
    number: 1,
    title: 'Connect with Loved Ones',
    description: 'Invite family members or friends you want to keep tabs on. They choose their own daily activity signal.'
  },
  {
    number: 2,
    title: 'Automatic Updates',
    description: "Check-ins are detected automatically through their chosen activity. You won't see what the activity was; you'll simply get a daily signal that they've checked in, keeping you reassured while respecting their privacy."
  },
  {
    number: 3,
    title: 'Stay Reassured',
    description: "Rest easy knowing you'll be notified if someone doesn't check in as expected."
  }
];
